import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { apiCall } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Download, Clock, Search, ChevronLeft, ChevronRight,
  MessageSquare, FileSpreadsheet, ArrowDownCircle, ArrowUpCircle, RefreshCw, Trash2,
  Calendar, Building2, Globe, TrendingUp, DollarSign, Activity
} from "lucide-react";

export default function B2CReportsPage() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const p = searchParams.get("tab");
    if (p === "delayed" || p === "delayed_deposits" || !p) return "delayed_deposits";
    if (p === "delayed_withdrawals") return "delayed_withdrawals";
    return p;
  });

  // Date range filter — default to today
  const todayStr = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(todayStr);
  const [dateTo, setDateTo] = useState(todayStr);

  // Branch & Site filters
  const [branchFilter, setBranchFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");
  const [availableBranches, setAvailableBranches] = useState([]);
  const [availableSites, setAvailableSites] = useState([]);

  // Summary stats for the selected date range & filters
  const [summaryStats, setSummaryStats] = useState({
    deposit_amount: 0,
    deposit_count: 0,
    avg_deposit: 0,
    avg_deposit_delay: 0,
    deposit_delayed_count: 0,
    withdraw_amount: 0,
    withdraw_count: 0,
    avg_withdraw: 0,
    avg_withdraw_delay: 0,
    withdraw_delayed_count: 0,
    net_amount: 0
  });
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Delayed Reports state
  const [delayedDeposits, setDelayedDeposits] = useState([]);
  const [delayedWithdrawals, setDelayedWithdrawals] = useState([]);
  const [loadingDelayed, setLoadingDelayed] = useState(false);

  // Reason / Remark Modal State
  const [remarkModalOpen, setRemarkModalOpen] = useState(false);
  const [selectedReportForRemark, setSelectedReportForRemark] = useState(null);
  const [remarkText, setRemarkText] = useState("");
  const [savingRemark, setSavingRemark] = useState(false);

  // All Deposit Reports tab state
  const [depositReports, setDepositReports] = useState([]);
  const [depositTotal, setDepositTotal] = useState(0);
  const [depositTotalAmount, setDepositTotalAmount] = useState(0);
  const [depositPage, setDepositPage] = useState(1);
  const [depositPages, setDepositPages] = useState(1);
  const [depositSearch, setDepositSearch] = useState("");
  const [loadingDeposits, setLoadingDeposits] = useState(false);

  // All Withdraw Reports tab state
  const [withdrawReports, setWithdrawReports] = useState([]);
  const [withdrawTotal, setWithdrawTotal] = useState(0);
  const [withdrawTotalAmount, setWithdrawTotalAmount] = useState(0);
  const [withdrawPage, setWithdrawPage] = useState(1);
  const [withdrawPages, setWithdrawPages] = useState(1);
  const [withdrawSearch, setWithdrawSearch] = useState("");
  const [loadingWithdraws, setLoadingWithdraws] = useState(false);

  // Helper to format the selected date label
  const getDateLabel = () => {
    if (dateFrom === dateTo) {
      if (dateFrom === todayStr) return "Today";
      return dateFrom;
    }
    return `${dateFrom} to ${dateTo}`;
  };

  // Quick Date Presets
  const setQuickDate = (preset) => {
    const today = new Date();
    if (preset === "today") {
      const d = today.toISOString().slice(0, 10);
      setDateFrom(d);
      setDateTo(d);
    } else if (preset === "yesterday") {
      const yest = new Date(today);
      yest.setDate(yest.getDate() - 1);
      const d = yest.toISOString().slice(0, 10);
      setDateFrom(d);
      setDateTo(d);
    } else if (preset === "7days") {
      const past7 = new Date(today);
      past7.setDate(past7.getDate() - 6);
      setDateFrom(past7.toISOString().slice(0, 10));
      setDateTo(today.toISOString().slice(0, 10));
    } else if (preset === "month") {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(firstDay.toISOString().slice(0, 10));
      setDateTo(today.toISOString().slice(0, 10));
    }
  };

  // Fetch Filters
  const fetchFilters = async () => {
    try {
      const { data } = await apiCall("get", "/reports/filters");
      if (data.branches) setAvailableBranches(data.branches);
      if (data.sites) setAvailableSites(data.sites);
    } catch (err) {
      console.error("Failed to load report filters", err);
    }
  };

  // Fetch Summary Stats
  const fetchSummaryStats = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (branchFilter && branchFilter !== "all") params.set("branch", branchFilter);
      if (siteFilter && siteFilter !== "all") params.set("site", siteFilter);
      const { data } = await apiCall("get", `/reports/summary-stats?${params}`);
      setSummaryStats(data);
    } catch (err) {
      console.error("Failed to fetch summary stats", err);
    } finally {
      setLoadingSummary(false);
    }
  }, [dateFrom, dateTo, branchFilter, siteFilter]);

  // Fetch Delayed Reports
  const fetchDelayedReports = useCallback(async () => {
    setLoadingDelayed(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (branchFilter && branchFilter !== "all") params.set("branch", branchFilter);
      if (siteFilter && siteFilter !== "all") params.set("site", siteFilter);
      const [depRes, wdrRes] = await Promise.all([
        apiCall("get", `/reports/delayed-deposit-reports?${params}`),
        apiCall("get", `/reports/delayed-withdraw-reports?${params}`)
      ]);
      setDelayedDeposits(depRes.data.reports || []);
      setDelayedWithdrawals(wdrRes.data.reports || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load delayed reports");
    } finally {
      setLoadingDelayed(false);
    }
  }, [dateFrom, dateTo, branchFilter, siteFilter]);

  // Fetch All Deposit Reports
  const fetchDepositReports = useCallback(async (page = 1) => {
    setLoadingDeposits(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: "50" });
      if (depositSearch) params.set("search", depositSearch);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (branchFilter && branchFilter !== "all") params.set("branch", branchFilter);
      if (siteFilter && siteFilter !== "all") params.set("site", siteFilter);
      const { data } = await apiCall("get", `/reports/deposit-reports?${params}`);
      setDepositReports(data.reports || []);
      setDepositTotal(data.total || 0);
      setDepositTotalAmount(data.total_amount || 0);
      setDepositPage(data.page || 1);
      setDepositPages(data.pages || 1);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load deposit reports");
    } finally {
      setLoadingDeposits(false);
    }
  }, [dateFrom, dateTo, branchFilter, siteFilter, depositSearch]);

  // Fetch All Withdraw Reports
  const fetchWithdrawReports = useCallback(async (page = 1) => {
    setLoadingWithdraws(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: "50" });
      if (withdrawSearch) params.set("search", withdrawSearch);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (branchFilter && branchFilter !== "all") params.set("branch", branchFilter);
      if (siteFilter && siteFilter !== "all") params.set("site", siteFilter);
      const { data } = await apiCall("get", `/reports/withdraw-reports?${params}`);
      setWithdrawReports(data.reports || []);
      setWithdrawTotal(data.total || 0);
      setWithdrawTotalAmount(data.total_amount || 0);
      setWithdrawPage(data.page || 1);
      setWithdrawPages(data.pages || 1);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load withdraw reports");
    } finally {
      setLoadingWithdraws(false);
    }
  }, [dateFrom, dateTo, branchFilter, siteFilter, withdrawSearch]);

  // Initial load
  useEffect(() => {
    fetchFilters();
  }, []);

  // When filters or date range change, reload summary stats and active tab data
  useEffect(() => {
    fetchSummaryStats();
    if (tab === "delayed_deposits" || tab === "delayed_withdrawals" || tab === "delayed") {
      fetchDelayedReports();
    }
    if (tab === "deposits") {
      fetchDepositReports(depositPage);
    } else if (tab === "withdrawals") {
      fetchWithdrawReports(withdrawPage);
    }
  }, [tab, dateFrom, dateTo, branchFilter, siteFilter, fetchSummaryStats, fetchDelayedReports, fetchDepositReports, fetchWithdrawReports, depositPage, withdrawPage]);

  // Download Delayed Reports CSV
  const downloadDelayedReport = async (type) => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (branchFilter && branchFilter !== "all") params.set("branch", branchFilter);
      if (siteFilter && siteFilter !== "all") params.set("site", siteFilter);
      const url = type === "deposits"
        ? `${process.env.REACT_APP_BACKEND_URL}/api/reports/download-delayed-deposits?${params}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/reports/download-delayed-withdrawals?${params}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include"
      });
      const blob = await response.blob();
      const fileUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = fileUrl;
      a.download = `delayed_${type}_report_${dateFrom || 'all'}_to_${dateTo || 'all'}.csv`;
      a.click();
      URL.revokeObjectURL(fileUrl);
      toast.success(`Delayed ${type} report downloaded`);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  // Remark Modal handlers
  const handleOpenRemarkModal = (report, reportType) => {
    setSelectedReportForRemark({ ...report, report_type: reportType });
    setRemarkText(report.remark || "");
    setRemarkModalOpen(true);
  };

  const handleSaveRemark = async () => {
    if (!selectedReportForRemark) return;
    setSavingRemark(true);
    try {
      await apiCall("post", "/reports/update-remark", {
        report_type: selectedReportForRemark.report_type,
        user_id: selectedReportForRemark.user_id || "",
        utr: selectedReportForRemark.utr || "",
        request_time: selectedReportForRemark.request_time || "",
        remark: remarkText
      });
      toast.success("Reason for delay saved!");

      if (selectedReportForRemark.report_type === "deposit") {
        setDelayedDeposits(prev => prev.map(r => 
          (r.utr && r.utr === selectedReportForRemark.utr) || (r.user_id === selectedReportForRemark.user_id && r.request_time === selectedReportForRemark.request_time)
            ? { ...r, remark: remarkText }
            : r
        ));
        setDepositReports(prev => prev.map(r => 
          (r.utr && r.utr === selectedReportForRemark.utr) || (r.user_id === selectedReportForRemark.user_id && r.request_time === selectedReportForRemark.request_time)
            ? { ...r, remark: remarkText }
            : r
        ));
      } else {
        setDelayedWithdrawals(prev => prev.map(r => 
          (r.utr && r.utr === selectedReportForRemark.utr) || (r.user_id === selectedReportForRemark.user_id && r.request_time === selectedReportForRemark.request_time)
            ? { ...r, remark: remarkText }
            : r
        ));
        setWithdrawReports(prev => prev.map(r => 
          (r.utr && r.utr === selectedReportForRemark.utr) || (r.user_id === selectedReportForRemark.user_id && r.request_time === selectedReportForRemark.request_time)
            ? { ...r, remark: remarkText }
            : r
        ));
      }
      setRemarkModalOpen(false);
    } catch (err) {
      toast.error("Failed to save remark");
    } finally {
      setSavingRemark(false);
    }
  };

  const handleDeleteReport = async (report, reportType) => {
    if (!window.confirm("Are you sure you want to delete this delayed report?")) return;
    try {
      await apiCall("post", "/reports/delete", {
        report_type: reportType,
        user_id: report.user_id || "",
        utr: report.utr || "",
        request_time: report.request_time || ""
      });
      toast.success("Delayed report deleted successfully!");
      
      // Refresh lists & summary
      fetchSummaryStats();
      fetchDelayedReports();
      if (tab === "deposits") {
        fetchDepositReports(depositPage);
      } else if (tab === "withdrawals") {
        fetchWithdrawReports(withdrawPage);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete report");
    }
  };

  return (
    <div className="space-y-6" data-testid="b2c-reports-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2.5">
            <FileSpreadsheet className="w-7 h-7 text-primary" />
            B2C Reports
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Audit and export Delayed Deposits and Delayed Withdrawals with branch &amp; site filters.
          </p>
        </div>

        {/* Global Controls: Quick Date Presets, Date Range & Branch/Site Filters */}
        <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-card border border-border">
          {/* Quick Date Presets */}
          <div className="flex items-center gap-1">
            <Button
              variant={dateFrom === todayStr && dateTo === todayStr ? "default" : "outline"}
              size="sm" className="text-xs h-8 px-2.5 font-medium"
              onClick={() => setQuickDate("today")}
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="sm" className="text-xs h-8 px-2.5 font-medium"
              onClick={() => setQuickDate("yesterday")}
            >
              Yesterday
            </Button>
            <Button
              variant="outline"
              size="sm" className="text-xs h-8 px-2.5 font-medium hidden sm:inline-flex"
              onClick={() => setQuickDate("7days")}
            >
              7 Days
            </Button>
            <Button
              variant="outline"
              size="sm" className="text-xs h-8 px-2.5 font-medium hidden md:inline-flex"
              onClick={() => setQuickDate("month")}
            >
              This Month
            </Button>
          </div>

          {/* Date Picker Range */}
          <div className="flex items-center gap-1.5 border-l border-border pl-2">
            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
            <Input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="w-32 h-8 text-xs font-mono"
              title="From Date"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <Input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="w-32 h-8 text-xs font-mono"
              title="To Date"
            />
          </div>

          {/* Branch Filter */}
          <div className="flex items-center gap-1 border-l border-border pl-2">
            <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
            <Select value={branchFilter} onValueChange={v => setBranchFilter(v)}>
              <SelectTrigger className="w-32 h-8 text-xs">
                <SelectValue placeholder="All Branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {availableBranches.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Site Filter */}
          <div className="flex items-center gap-1">
            <Globe className="w-3.5 h-3.5 text-muted-foreground" />
            <Select value={siteFilter} onValueChange={v => setSiteFilter(v)}>
              <SelectTrigger className="w-32 h-8 text-xs">
                <SelectValue placeholder="All Sites" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sites</SelectItem>
                {availableSites.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Button
            size="sm"
            variant="ghost"
            className="h-8 px-2 text-muted-foreground hover:text-foreground"
            onClick={() => {
              fetchSummaryStats();
              if (tab === "delayed_deposits" || tab === "delayed_withdrawals") fetchDelayedReports();
              else if (tab === "deposits") fetchDepositReports(depositPage);
              else if (tab === "withdrawals") fetchWithdrawReports(withdrawPage);
            }}
            title="Refresh All"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingSummary ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Top Summary Cards — Dedicated to Total & Delayed Deposits and Withdrawals */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Deposits Card */}
        <Card className="border border-emerald-500/20 bg-emerald-500/5 relative overflow-hidden">
          <div className="absolute right-3 top-3 opacity-15 text-emerald-600">
            <ArrowDownCircle className="w-12 h-12" />
          </div>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                Total Deposits ({getDateLabel()})
              </span>
              <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-600 bg-emerald-500/10">
                {summaryStats.deposit_count} Txns
              </Badge>
            </div>
            <p className="text-2xl font-black text-emerald-600 font-mono mt-1">
              ₹{Number(summaryStats.deposit_amount).toLocaleString('en-IN')}
            </p>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground mt-2 pt-2 border-t border-emerald-500/10">
              <span>Avg: ₹{Math.round(summaryStats.avg_deposit).toLocaleString('en-IN')}</span>
              <span>Avg Delay: {summaryStats.avg_deposit_delay}m</span>
            </div>
          </CardContent>
        </Card>

        {/* Total Withdrawals Card */}
        <Card className="border border-rose-500/20 bg-rose-500/5 relative overflow-hidden">
          <div className="absolute right-3 top-3 opacity-15 text-rose-600">
            <ArrowUpCircle className="w-12 h-12" />
          </div>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-rose-700 dark:text-rose-400">
                Total Withdrawals ({getDateLabel()})
              </span>
              <Badge variant="outline" className="text-[10px] border-rose-500/30 text-rose-600 bg-rose-500/10">
                {summaryStats.withdraw_count} Txns
              </Badge>
            </div>
            <p className="text-2xl font-black text-rose-600 font-mono mt-1">
              ₹{Number(summaryStats.withdraw_amount).toLocaleString('en-IN')}
            </p>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground mt-2 pt-2 border-t border-rose-500/10">
              <span>Avg: ₹{Math.round(summaryStats.avg_withdraw).toLocaleString('en-IN')}</span>
              <span>Avg Delay: {summaryStats.avg_withdraw_delay}m</span>
            </div>
          </CardContent>
        </Card>

        {/* Delayed Deposits Card */}
        <Card className="border border-orange-500/20 bg-orange-500/5 relative overflow-hidden">
          <div className="absolute right-3 top-3 opacity-15 text-orange-600">
            <Clock className="w-12 h-12" />
          </div>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-orange-700 dark:text-orange-400">
                Delayed Deposits
              </span>
              <Badge variant="outline" className="text-[10px] border-orange-500/30 text-orange-600 bg-orange-500/10">
                Audit
              </Badge>
            </div>
            <p className="text-2xl font-black text-orange-600 font-mono mt-1">
              {summaryStats.deposit_delayed_count}
            </p>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground mt-2 pt-2 border-t border-orange-500/10">
              <span>Avg Dep Delay: {summaryStats.avg_deposit_delay}m</span>
              <button
                onClick={() => downloadDelayedReport("deposits")}
                className="text-orange-600 hover:underline font-semibold flex items-center gap-1"
                title="Download CSV"
              >
                <Download className="w-3 h-3" /> CSV
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Delayed Withdrawals Card */}
        <Card className="border border-red-500/20 bg-red-500/5 relative overflow-hidden">
          <div className="absolute right-3 top-3 opacity-15 text-red-600">
            <Clock className="w-12 h-12" />
          </div>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-red-700 dark:text-red-400">
                Delayed Withdrawals
              </span>
              <Badge variant="outline" className="text-[10px] border-red-500/30 text-red-600 bg-red-500/10">
                Audit
              </Badge>
            </div>
            <p className="text-2xl font-black text-red-600 font-mono mt-1">
              {summaryStats.withdraw_delayed_count}
            </p>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground mt-2 pt-2 border-t border-red-500/10">
              <span>Avg Wdr Delay: {summaryStats.avg_withdraw_delay}m</span>
              <button
                onClick={() => downloadDelayedReport("withdrawals")}
                className="text-red-600 hover:underline font-semibold flex items-center gap-1"
                title="Download CSV"
              >
                <Download className="w-3 h-3" /> CSV
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs — Explicit Delayed Deposits & Delayed Withdrawals */}
      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-muted/60 p-1 flex-wrap h-auto">
          <TabsTrigger value="delayed_deposits" className="flex items-center gap-2 text-xs">
            <Clock className="w-3.5 h-3.5 text-orange-500" />
            Delayed Deposits ({delayedDeposits.length})
          </TabsTrigger>
          <TabsTrigger value="delayed_withdrawals" className="flex items-center gap-2 text-xs">
            <Clock className="w-3.5 h-3.5 text-red-500" />
            Delayed Withdrawals ({delayedWithdrawals.length})
          </TabsTrigger>
          <TabsTrigger value="deposits" className="flex items-center gap-2 text-xs">
            <ArrowDownCircle className="w-3.5 h-3.5 text-emerald-500" />
            All Deposits ({depositTotal})
          </TabsTrigger>
          <TabsTrigger value="withdrawals" className="flex items-center gap-2 text-xs">
            <ArrowUpCircle className="w-3.5 h-3.5 text-rose-500" />
            All Withdrawals ({withdrawTotal})
          </TabsTrigger>
        </TabsList>

        {/* ── TAB 1: DELAYED DEPOSIT REPORTS ── */}
        <TabsContent value="delayed_deposits" className="space-y-4">
          <Card className="border border-border">
            <CardHeader className="pb-3 pt-4 px-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <Clock className="w-4 h-4 text-orange-500" /> Delayed Deposit Reports ({delayedDeposits.length})
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => downloadDelayedReport("deposits")} className="text-xs h-8">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Download Deposits CSV
                </Button>
                <Button size="sm" variant="outline" onClick={fetchDelayedReports} className="text-xs h-8">
                  <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loadingDelayed ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loadingDelayed ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Loading delayed deposits...</div>
              ) : delayedDeposits.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No delayed deposits found for {getDateLabel()}.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-[10px] uppercase">User ID</TableHead>
                        <TableHead className="text-[10px] uppercase">Loginname</TableHead>
                        <TableHead className="text-[10px] uppercase">Amount</TableHead>
                        <TableHead className="text-[10px] uppercase">Branch</TableHead>
                        <TableHead className="text-[10px] uppercase">Site Name</TableHead>
                        <TableHead className="text-[10px] uppercase">Delay</TableHead>
                        <TableHead className="text-[10px] uppercase">Req Time</TableHead>
                        <TableHead className="text-[10px] uppercase">Reason / Remark</TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {delayedDeposits.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs font-semibold">{r.user_id}</TableCell>
                          <TableCell className="text-xs">{r.loginname}</TableCell>
                          <TableCell className="text-xs font-medium text-emerald-600 font-mono">₹{r.amount}</TableCell>
                          <TableCell className="text-xs">{r.branchname || r.branch || "-"}</TableCell>
                          <TableCell className="text-xs">{r.site || "-"}</TableCell>
                          <TableCell className="text-xs font-bold text-orange-600">{r.approve_delay_in_mins} min</TableCell>
                          <TableCell className="text-[10px] whitespace-nowrap">{r.request_time}</TableCell>
                          <TableCell className="text-xs max-w-[150px] truncate">
                            {r.remark ? (
                              <span className="text-foreground font-medium">{r.remark}</span>
                            ) : (
                              <span className="text-muted-foreground italic text-[11px]">No reason added</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <div className="flex gap-1.5 justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenRemarkModal(r, "deposit")}
                                className="text-xs h-7 px-2.5 flex items-center gap-1.5 border-primary/40 bg-primary/5 hover:bg-primary/10 text-primary font-medium"
                              >
                                <MessageSquare className="w-3.5 h-3.5" />
                                {r.remark ? "Edit Reason" : "+ Add Reason"}
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteReport(r, "deposit")}
                                className="text-xs h-7 px-2"
                                title="Delete Report"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── TAB 2: DELAYED WITHDRAWAL REPORTS ── */}
        <TabsContent value="delayed_withdrawals" className="space-y-4">
          <Card className="border border-border">
            <CardHeader className="pb-3 pt-4 px-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <Clock className="w-4 h-4 text-red-500" /> Delayed Withdrawal Reports ({delayedWithdrawals.length})
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => downloadDelayedReport("withdrawals")} className="text-xs h-8">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Download Withdrawals CSV
                </Button>
                <Button size="sm" variant="outline" onClick={fetchDelayedReports} className="text-xs h-8">
                  <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loadingDelayed ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loadingDelayed ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Loading delayed withdrawals...</div>
              ) : delayedWithdrawals.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No delayed withdrawals found for {getDateLabel()}.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-[10px] uppercase">User ID</TableHead>
                        <TableHead className="text-[10px] uppercase">Loginname</TableHead>
                        <TableHead className="text-[10px] uppercase">Amount</TableHead>
                        <TableHead className="text-[10px] uppercase">Branch</TableHead>
                        <TableHead className="text-[10px] uppercase">Site Name</TableHead>
                        <TableHead className="text-[10px] uppercase">Delay</TableHead>
                        <TableHead className="text-[10px] uppercase">Req Time</TableHead>
                        <TableHead className="text-[10px] uppercase">Reason / Remark</TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {delayedWithdrawals.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs font-semibold">{r.user_id}</TableCell>
                          <TableCell className="text-xs">{r.loginname}</TableCell>
                          <TableCell className="text-xs font-medium text-rose-600 font-mono">₹{r.amount}</TableCell>
                          <TableCell className="text-xs">{r.branchname || r.branch || "-"}</TableCell>
                          <TableCell className="text-xs">{r.site || "-"}</TableCell>
                          <TableCell className="text-xs font-bold text-red-600">{r.approve_delay_in_mins} min</TableCell>
                          <TableCell className="text-[10px] whitespace-nowrap">{r.request_time}</TableCell>
                          <TableCell className="text-xs max-w-[150px] truncate">
                            {r.remark ? (
                              <span className="text-foreground font-medium">{r.remark}</span>
                            ) : (
                              <span className="text-muted-foreground italic text-[11px]">No reason added</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <div className="flex gap-1.5 justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenRemarkModal(r, "withdraw")}
                                className="text-xs h-7 px-2.5 flex items-center gap-1.5 border-rose-500/40 bg-rose-500/5 hover:bg-rose-500/10 text-rose-600 font-medium"
                              >
                                <MessageSquare className="w-3.5 h-3.5" />
                                {r.remark ? "Edit Reason" : "+ Add Reason"}
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteReport(r, "withdraw")}
                                className="text-xs h-7 px-2"
                                title="Delete Report"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── TAB 2: ALL DEPOSIT REPORTS ── */}
        <TabsContent value="deposits" className="space-y-4">
          <Card className="border border-border">
            <CardHeader className="pb-3 pt-4 px-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by User ID, Loginname, UTR, Bank..."
                  value={depositSearch}
                  onChange={e => setDepositSearch(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") fetchDepositReports(1); }}
                  className="w-72 h-8 text-xs"
                />
                <Button size="sm" variant="secondary" onClick={() => fetchDepositReports(1)} className="text-xs h-8">
                  Search
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  Total: <strong className="text-foreground">₹{Number(depositTotalAmount).toLocaleString('en-IN')}</strong> ({depositTotal} txns)
                </span>
                <Button size="sm" variant="outline" onClick={() => fetchDepositReports(depositPage)} className="text-xs h-8">
                  <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loadingDeposits ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loadingDeposits ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Loading deposit reports...</div>
              ) : depositReports.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No deposit reports found for {getDateLabel()}.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-[10px] uppercase">User ID</TableHead>
                        <TableHead className="text-[10px] uppercase">Loginname</TableHead>
                        <TableHead className="text-[10px] uppercase">Amount</TableHead>
                        <TableHead className="text-[10px] uppercase">Branch</TableHead>
                        <TableHead className="text-[10px] uppercase">Site Name</TableHead>
                        <TableHead className="text-[10px] uppercase">UTR</TableHead>
                        <TableHead className="text-[10px] uppercase">Bank</TableHead>
                        <TableHead className="text-[10px] uppercase">Delay</TableHead>
                        <TableHead className="text-[10px] uppercase">Date</TableHead>
                        <TableHead className="text-[10px] uppercase">Request Time</TableHead>
                        <TableHead className="text-[10px] uppercase">Approval Time</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {depositReports.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs font-semibold">{r.user_id}</TableCell>
                          <TableCell className="text-xs">{r.loginname}</TableCell>
                          <TableCell className="text-xs font-medium text-emerald-600 font-mono">₹{r.amount}</TableCell>
                          <TableCell className="text-xs">{r.branchname || r.branch || "-"}</TableCell>
                          <TableCell className="text-xs">{r.site || "-"}</TableCell>
                          <TableCell className="text-xs font-mono text-muted-foreground">{r.utr || "-"}</TableCell>
                          <TableCell className="text-xs">{r.payment_bank || "-"}</TableCell>
                          <TableCell className="text-xs font-bold text-orange-600">
                            {r.approve_delay_in_mins != null ? `${r.approve_delay_in_mins} min` : "-"}
                          </TableCell>
                          <TableCell className="text-[10px] whitespace-nowrap font-mono">{r.date || (r.approval_time ? r.approval_time.slice(0, 10) : "-")}</TableCell>
                          <TableCell className="text-[10px] whitespace-nowrap">{r.request_time || "-"}</TableCell>
                          <TableCell className="text-[10px] whitespace-nowrap">{r.approval_time || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Pagination */}
              {depositPages > 1 && (
                <div className="flex items-center justify-between p-3 border-t border-border">
                  <span className="text-xs text-muted-foreground">
                    Page {depositPage} of {depositPages} ({depositTotal} total)
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline" size="sm" className="h-7 text-xs"
                      disabled={depositPage <= 1}
                      onClick={() => fetchDepositReports(depositPage - 1)}
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="outline" size="sm" className="h-7 text-xs"
                      disabled={depositPage >= depositPages}
                      onClick={() => fetchDepositReports(depositPage + 1)}
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── TAB 3: ALL WITHDRAW REPORTS ── */}
        <TabsContent value="withdrawals" className="space-y-4">
          <Card className="border border-border">
            <CardHeader className="pb-3 pt-4 px-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by User ID, Loginname, UTR..."
                  value={withdrawSearch}
                  onChange={e => setWithdrawSearch(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") fetchWithdrawReports(1); }}
                  className="w-72 h-8 text-xs"
                />
                <Button size="sm" variant="secondary" onClick={() => fetchWithdrawReports(1)} className="text-xs h-8">
                  Search
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  Total: <strong className="text-foreground">₹{Number(withdrawTotalAmount).toLocaleString('en-IN')}</strong> ({withdrawTotal} txns)
                </span>
                <Button size="sm" variant="outline" onClick={() => fetchWithdrawReports(withdrawPage)} className="text-xs h-8">
                  <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loadingWithdraws ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loadingWithdraws ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Loading withdraw reports...</div>
              ) : withdrawReports.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No withdraw reports found for {getDateLabel()}.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-[10px] uppercase">User ID</TableHead>
                        <TableHead className="text-[10px] uppercase">Loginname</TableHead>
                        <TableHead className="text-[10px] uppercase">Amount</TableHead>
                        <TableHead className="text-[10px] uppercase">Branch</TableHead>
                        <TableHead className="text-[10px] uppercase">Site Name</TableHead>
                        <TableHead className="text-[10px] uppercase">UTR</TableHead>
                        <TableHead className="text-[10px] uppercase">Delay</TableHead>
                        <TableHead className="text-[10px] uppercase">Date</TableHead>
                        <TableHead className="text-[10px] uppercase">Request Time</TableHead>
                        <TableHead className="text-[10px] uppercase">Approval Time</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {withdrawReports.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs font-semibold">{r.user_id}</TableCell>
                          <TableCell className="text-xs">{r.loginname}</TableCell>
                          <TableCell className="text-xs font-medium text-rose-600 font-mono">₹{r.amount}</TableCell>
                          <TableCell className="text-xs">{r.branchname || r.branch || "-"}</TableCell>
                          <TableCell className="text-xs">{r.site || "-"}</TableCell>
                          <TableCell className="text-xs font-mono text-muted-foreground">{r.utr || "-"}</TableCell>
                          <TableCell className="text-xs font-bold text-red-600">
                            {r.approve_delay_in_mins != null ? `${r.approve_delay_in_mins} min` : "-"}
                          </TableCell>
                          <TableCell className="text-[10px] whitespace-nowrap font-mono">{r.date || (r.approval_time ? r.approval_time.slice(0, 10) : "-")}</TableCell>
                          <TableCell className="text-[10px] whitespace-nowrap">{r.request_time || "-"}</TableCell>
                          <TableCell className="text-[10px] whitespace-nowrap">{r.approval_time || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Pagination */}
              {withdrawPages > 1 && (
                <div className="flex items-center justify-between p-3 border-t border-border">
                  <span className="text-xs text-muted-foreground">
                    Page {withdrawPage} of {withdrawPages} ({withdrawTotal} total)
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline" size="sm" className="h-7 text-xs"
                      disabled={withdrawPage <= 1}
                      onClick={() => fetchWithdrawReports(withdrawPage - 1)}
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="outline" size="sm" className="h-7 text-xs"
                      disabled={withdrawPage >= withdrawPages}
                      onClick={() => fetchWithdrawReports(withdrawPage + 1)}
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* POPUP MODAL: SUBMIT REASON FOR DELAY */}
      <Dialog open={remarkModalOpen} onOpenChange={setRemarkModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-primary" />
              Submit Delay Reason / Remark
            </DialogTitle>
          </DialogHeader>
          {selectedReportForRemark && (
            <div className="space-y-3 pt-2">
              <div className="p-3 bg-muted/40 rounded-lg space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">User ID:</span>
                  <span className="font-semibold">{selectedReportForRemark.user_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Loginname:</span>
                  <span className="font-semibold">{selectedReportForRemark.loginname}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Branch:</span>
                  <span className="font-semibold">{selectedReportForRemark.branchname || selectedReportForRemark.branch || "-"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount:</span>
                  <span className="font-mono font-bold">₹{selectedReportForRemark.amount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Approval Delay:</span>
                  <span className="font-bold text-orange-600">{selectedReportForRemark.approve_delay_in_mins} minutes</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Request Time:</span>
                  <span>{selectedReportForRemark.request_time}</span>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold">Reason for Delay:</label>
                <textarea
                  className="w-full h-24 p-2.5 text-xs rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Enter detailed reason for the approval delay (e.g. Bank portal server downtime, UTR mismatch check, Manual verification required)..."
                  value={remarkText}
                  onChange={e => setRemarkText(e.target.value)}
                />
              </div>

              <DialogFooter className="flex gap-2 sm:justify-end">
                <Button variant="outline" size="sm" onClick={() => setRemarkModalOpen(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSaveRemark} disabled={savingRemark}>
                  {savingRemark ? "Saving..." : "Save Reason"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
